import { useCallback, useMemo, useState } from 'react'

import { FILTER_TYPE_ORDER } from '../constants/reviewEntriesFilterConfig'
import { ReviewEntriesFilterType } from '../enums/reviewEntriesFilterType'
import { ReviewEntriesViewFilter } from '../enums/reviewEntriesViewFilter'
import type { ReviewEntriesFiltersState, ReviewEntriesFilterValue } from '../types/reviewEntriesFilters'
import type { ReviewEntryTableRow } from '../types/reviewEntryTableRow'
import { countToValidateDocuments, filterReviewEntriesTableRows } from '../utils/filterReviewEntriesTableRows'
import {
  getActiveReviewEntriesFilterTypes,
  isReviewEntriesFilterValueEmpty,
} from '../utils/isReviewEntriesFilterValueEmpty'

export interface UseReviewEntriesTableFiltersResult {
  activeTypes: ReviewEntriesFilterType[]
  addedTypes: ReviewEntriesFilterType[]
  autoOpenFilterType: ReviewEntriesFilterType | null
  clearAutoOpenFilterType: () => void
  filteredRows: ReviewEntryTableRow[]
  filters: ReviewEntriesFiltersState
  isFiltersBarVisible: boolean
  addFilter: (type: ReviewEntriesFilterType) => void
  removeFilter: (type: ReviewEntriesFilterType) => void
  resetAllFilters: () => void
  resetFilter: (type: ReviewEntriesFilterType) => void
  searchQuery: string
  setFilterValue: <T extends ReviewEntriesFilterType>(type: T, value: ReviewEntriesFilterValue<T>) => void
  setSearchQuery: (value: string) => void
  setViewFilter: (value: ReviewEntriesViewFilter) => void
  toValidateCount: number
  viewFilter: ReviewEntriesViewFilter
}

export const useReviewEntriesTableFilters = (rows: ReviewEntryTableRow[]): UseReviewEntriesTableFiltersResult => {
  const [searchQuery, setSearchQuery] = useState('')
  const [viewFilter, setViewFilter] = useState(ReviewEntriesViewFilter.ToValidate)
  const [filters, setFilters] = useState<ReviewEntriesFiltersState>({})
  const [addedTypes, setAddedTypes] = useState<ReviewEntriesFilterType[]>([])
  const [autoOpenFilterType, setAutoOpenFilterType] = useState<ReviewEntriesFilterType | null>(null)

  const toValidateCount = useMemo(() => countToValidateDocuments(rows), [rows])

  const activeTypes = useMemo(() => getActiveReviewEntriesFilterTypes(filters), [filters])

  const isFiltersBarVisible = activeTypes.length > 0 || addedTypes.length > 0

  const filteredRows = useMemo(
    () => filterReviewEntriesTableRows(rows, searchQuery, viewFilter, filters),
    [filters, rows, searchQuery, viewFilter],
  )

  const handleSetSearchQuery = useCallback((value: string) => {
    setSearchQuery(value)
  }, [])

  const handleSetViewFilter = useCallback((value: ReviewEntriesViewFilter) => {
    setViewFilter(value)
  }, [])

  const setFilterValue = useCallback(
    <T extends ReviewEntriesFilterType>(type: T, value: ReviewEntriesFilterValue<T>) => {
      setFilters((previousFilters) => {
        if (isReviewEntriesFilterValueEmpty(type, value)) {
          const nextFilters = { ...previousFilters }
          delete nextFilters[type]

          return nextFilters
        }

        return {
          ...previousFilters,
          [type]: value,
        }
      })
      setAddedTypes((previousAddedTypes) =>
        previousAddedTypes.includes(type) ? previousAddedTypes : [...previousAddedTypes, type],
      )
    },
    [],
  )

  const addFilter = useCallback((type: ReviewEntriesFilterType) => {
    setAddedTypes((previousAddedTypes) =>
      previousAddedTypes.includes(type) ? previousAddedTypes : [...previousAddedTypes, type],
    )
    setAutoOpenFilterType(type)
  }, [])

  const removeFilter = useCallback((type: ReviewEntriesFilterType) => {
    setFilters((previousFilters) => {
      const nextFilters = { ...previousFilters }
      delete nextFilters[type]

      return nextFilters
    })
    setAddedTypes((previousAddedTypes) => previousAddedTypes.filter((addedType) => addedType !== type))
    setAutoOpenFilterType((previousAutoOpenFilterType) =>
      previousAutoOpenFilterType === type ? null : previousAutoOpenFilterType,
    )
  }, [])

  const resetFilter = useCallback((type: ReviewEntriesFilterType) => {
    setFilters((previousFilters) => {
      const nextFilters = { ...previousFilters }
      delete nextFilters[type]

      return nextFilters
    })
    setAddedTypes((previousAddedTypes) =>
      previousAddedTypes.includes(type) ? previousAddedTypes : [...previousAddedTypes, type],
    )
  }, [])

  const resetAllFilters = useCallback(() => {
    setFilters({})
    setAddedTypes([])
    setAutoOpenFilterType(null)
  }, [])

  const clearAutoOpenFilterType = useCallback(() => {
    setAutoOpenFilterType(null)
  }, [])

  const visibleFilterTypes = useMemo(
    () => FILTER_TYPE_ORDER.filter((type) => activeTypes.includes(type) || addedTypes.includes(type)),
    [activeTypes, addedTypes],
  )

  return {
    activeTypes,
    addedTypes: visibleFilterTypes,
    autoOpenFilterType,
    clearAutoOpenFilterType,
    filteredRows,
    filters,
    isFiltersBarVisible,
    addFilter,
    removeFilter,
    resetAllFilters,
    resetFilter,
    searchQuery,
    setFilterValue,
    setSearchQuery: handleSetSearchQuery,
    setViewFilter: handleSetViewFilter,
    toValidateCount,
    viewFilter,
  }
}
